import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CreditCard, DollarSign, Printer, Download, Filter,
  ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Wallet
} from 'lucide-react';
import CustomerTransactionRow from './CustomerTransactionRow';

export default function CreditLedger({
  customer,
  transactions = [],
  loading = false,
  onExport,
  onPrint,
}) {
  const [page, setPage] = useState(1);
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const pageSize = 20;

  const summary = useMemo(() => {
    const totalCredit = transactions
      .filter((t) => t.type === 'credit')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
    const totalPayments = transactions
      .filter((t) => t.type === 'payment')
      .reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);
    const outstanding = totalCredit - totalPayments;
    return { totalCredit, totalPayments, outstanding };
  }, [transactions]);

  const filtered = useMemo(() => {
    let result = transactions;
    if (dateFilter.start) {
      result = result.filter((t) => new Date(t.date) >= new Date(dateFilter.start));
    }
    if (dateFilter.end) {
      result = result.filter((t) => new Date(t.date) <= new Date(dateFilter.end));
    }
    return result;
  }, [transactions, dateFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      {customer && (
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold">
              {(customer.name || '?').charAt(0).toUpperCase()}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{customer.name}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">{customer.phone}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 p-5 bg-gray-50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-700">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-1">
            <TrendingUp size={12} className="text-red-400" />
            Credit Given
          </div>
          <p className="text-lg font-bold text-red-600 dark:text-red-400">Rs. {summary.totalCredit.toLocaleString()}</p>
        </div>
        <div className="text-center border-x border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-1">
            <TrendingDown size={12} className="text-emerald-400" />
            Payments
          </div>
          <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">Rs. {summary.totalPayments.toLocaleString()}</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-xs text-gray-500 dark:text-gray-400 mb-1">
            <Wallet size={12} />
            Outstanding
          </div>
          <p className={`text-lg font-bold ${summary.outstanding > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
            Rs. {summary.outstanding.toLocaleString()}
          </p>
        </div>
      </div>

      <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3 flex-wrap">
        <Filter size={14} className="text-gray-400" />
        <input
          type="date"
          value={dateFilter.start}
          onChange={(e) => { setDateFilter((prev) => ({ ...prev, start: e.target.value })); setPage(1); }}
          className="text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1.5 text-gray-700 dark:text-gray-300 focus:outline-none"
        />
        <span className="text-xs text-gray-400">to</span>
        <input
          type="date"
          value={dateFilter.end}
          onChange={(e) => { setDateFilter((prev) => ({ ...prev, end: e.target.value })); setPage(1); }}
          className="text-xs bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1.5 text-gray-700 dark:text-gray-300 focus:outline-none"
        />
        {(dateFilter.start || dateFilter.end) && (
          <button
            onClick={() => setDateFilter({ start: '', end: '' })}
            className="text-xs text-red-500 hover:underline"
          >
            Clear
          </button>
        )}
        <div className="flex-1" />
        <button
          onClick={onExport}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <Download size={12} />
          Export
        </button>
        <button
          onClick={onPrint}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <Printer size={12} />
          Print
        </button>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4 animate-pulse">
              <div className="w-9 h-9 rounded-lg bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="h-2 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
              <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
            <CreditCard size={36} className="mb-2" />
            <p className="text-sm font-medium">No transactions found</p>
            <p className="text-xs mt-1">Transactions will appear here once credit is given</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            <div className="flex items-center gap-3 px-4 py-2 text-xs font-medium text-gray-400 dark:text-gray-500">
              <div className="w-9" />
              <div className="flex-1">Description</div>
              <div className="text-right w-24">Amount</div>
              <div className="text-right w-24">Balance</div>
            </div>
            <AnimatePresence mode="popLayout">
              {paginated.map((t, idx) => (
                <CustomerTransactionRow key={t.id || t._id || idx} transaction={t} index={idx} />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {filtered.length > pageSize && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400 px-2">{page} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
